import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  Renderer2,
  TemplateRef,
  input,
  model,
  output,
  viewChild,
} from '@angular/core';
import type { AnimationEvent } from '@angular/animations';
import { ClickOutsideDirective } from '@catch/directives';
import { ThemingService } from '@catch/services';
import { fadeInOutDropdownComponent } from './dropdown-animation';

const DROPDOWN_GAP_PX = 8;

@Component({
  selector: 'ct-lib-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  animations: [fadeInOutDropdownComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, ClickOutsideDirective],
})
export class DropdownComponent implements OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);

  dropdownElement = viewChild<ElementRef<HTMLElement>>('dropdownElement');
  toggleRef = viewChild<ElementRef<HTMLElement>>('toggleRef');
  dropdown = input<TemplateRef<HTMLElement>>();
  autowidth = input<boolean>(false);
  openState = model<boolean>(false);
  alignment = input<string>('right');
  disabled = input<boolean>(false);
  dropUp = model<boolean>(false);
  closeOnClick = input<boolean>(false);
  freezeState = input<boolean>(false);

  onOpen = output<void>();
  onClose = output<void>();

  private scrollResizeCleanup: (() => void) | null = null;

  constructor(
    public themingService: ThemingService,
    private cdr: ChangeDetectorRef
  ) {
    effect(() => {
      const open = this.openState();
      if (open) {
        const refineTimer = setTimeout(() => this.updateDropdownPosition(), 80);
        const onUpdate = () => this.updateDropdownPosition();
        window.addEventListener('scroll', onUpdate, true);
        window.addEventListener('resize', onUpdate);
        this.scrollResizeCleanup = () => {
          clearTimeout(refineTimer);
          window.removeEventListener('scroll', onUpdate, true);
          window.removeEventListener('resize', onUpdate);
          this.scrollResizeCleanup = null;
        };
      } else {
        this.scrollResizeCleanup?.();
      }
    });
  }

  open() {
    if (this.freezeState()) return;
    this.openState.set(true);
    this.onOpen.emit();
    this.cdr.detectChanges();
  }

  close() {
    if (this.freezeState()) return;
    this.openState.set(false);
    this.onClose.emit();
    this.cdr.detectChanges();
  }

  toggle() {
    if (this.openState()) {
      this.close();
    } else {
      this.open();
    }
  }

  /** Beim Start der Animation: Position sofort setzen, damit es von Frame 1 an stimmt. */
  onDropdownAnimationStart(event: AnimationEvent): void {
    if (event.fromState !== 'void') return;
    const el = event.element as HTMLElement;
    if (el) this.applyPositionToElement(el);
  }

  /** Nach Ende der Animation: Position erneut setzen (falls die Animation Styles überschrieben hat). */
  onDropdownAnimationDone(event: AnimationEvent): void {
    if (event.toState === 'void') return;
    const el = event.element as HTMLElement;
    if (el) this.applyPositionToElement(el);
  }

  ngOnDestroy(): void {
    this.scrollResizeCleanup?.();
  }

  private updateDropdownPosition(): void {
    const host = this.elementRef.nativeElement;
    const toggle =
      this.toggleRef()?.nativeElement ??
      (host.querySelector('.toggle') as HTMLElement | null);
    if (!toggle) return;

    const dropdownEl = host.querySelector('.dropdown') as HTMLElement | null;
    if (!dropdownEl) return;

    this.applyPositionToElement(dropdownEl);
    this.cdr.markForCheck();
  }

  private applyPositionToElement(dropdownEl: HTMLElement): void {
    const host = this.elementRef.nativeElement;
    const toggle =
      this.toggleRef()?.nativeElement ??
      (host.querySelector('.toggle') as HTMLElement | null);
    if (!toggle) return;

    const rect = toggle.getBoundingClientRect();
    const alignRight = this.alignment() === 'right';

    const dropdownHeight = dropdownEl.offsetHeight || 450;
    const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP_PX;
    const isDropUp = spaceBelow < dropdownHeight && rect.top > spaceBelow;
    this.dropUp.set(isDropUp);

    const top = rect.bottom + DROPDOWN_GAP_PX;
    const bottom = window.innerHeight - rect.top + DROPDOWN_GAP_PX;
    const right = window.innerWidth - rect.right;
    const isAutowidth = this.autowidth();

    const parts: string[] = [
      'position:fixed',
      alignRight ? `right:${right}px` : `left:${rect.left}px`,
      isDropUp ? '' : `top:${top}px`,
      isDropUp ? `bottom:${bottom}px` : '',
      isAutowidth ? 'width:fit-content' : `width:${rect.width}px`,
      isAutowidth ? '' : `min-width:${rect.width}px`,
    ];
    const styleStr = parts.filter(Boolean).join(';');

    this.renderer.setAttribute(dropdownEl, 'style', styleStr);
  }
}
